import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { fetchOrders, fetchOrderStats } from "../store/actions/orderActions";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/Table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import {
  Search,
  Filter,
  Download,
  ShoppingCart,
  AlertCircle,
  Clock,
  DollarSign,
  Store,
  MoreVertical,
  Eye,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { formatCurrency } from "../utils/formatCurrency";
import * as ratesService from "../services/ratesService";

const statKeys = [
  { nameKey: "orders.totalOrders", key: "totalOrders", icon: ShoppingCart },
  { nameKey: "orders.pending", key: "pendingOrders", icon: Clock },
  { nameKey: "orders.completed", key: "completedOrders", icon: ShoppingCart },
  { nameKey: "orders.revenue", key: "revenue", format: "currency", icon: DollarSign },
  { nameKey: "orders.stores", key: "activeStores", icon: Store },
];

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const Sub = styled.p`
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colors.text.body};
`;

const StatGrid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const StatCard = styled(Card)`
  padding: 20px;
`;

const StatTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.body};
`;

const StatValue = styled.div`
  margin-top: 6px;
  font-size: 26px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const IconBubble = styled.div`
  border-radius: 9999px;
  padding: 10px;
  background: "rgba(79,70,229,0.08)";
  color: #4f46e5;
`;

export default function Orders() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orders = [], stats: orderStats, loading } = useSelector((state) => state.order);
  const user = useSelector((state) => state.auth?.user);
  const [tab, setTab] = useState("overview");
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [rates, setRates] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [period, setPeriod] = useState("30d");
  const navigate = useNavigate();

  useEffect(() => {
    ratesService.getRates().then((r) => setRates(r.rates)).catch(() => setRates(null));
  }, []);

  useEffect(() => {
    dispatch(fetchOrders());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchOrderStats({ period: period || undefined }));
  }, [dispatch, period]);

  const baseCurrency = "USD";
  const stats = statKeys.map((s) => {
    const raw = orderStats != null && s.key in orderStats ? orderStats[s.key] : null;
    let value = loading && orderStats == null ? "…" : raw == null ? "—" : String(raw);
    if (s.format === "currency" && typeof raw === "number") {
      const converted = rates ? ratesService.convert(raw, baseCurrency, currency, rates) : raw;
      value = formatCurrency(converted, currency);
    }
    return { ...s, value };
  });

  const chartData = (orderStats?.ordersByDate?.length > 0)
    ? orderStats.ordersByDate.map((d) => ({ name: d.date, orders: d.orders, value: d.value }))
    : [{ name: t("common.noData"), orders: 0, value: 0 }];

  const getStatusStyle = (status) => {
    const map = {
      processing: { bg: "#dbeafe", fg: "#1d4ed8" },
      pending: { bg: "#fef9c3", fg: "#854d0e" },
      completed: { bg: "#dcfce7", fg: "#166534" },
      cancelled: { bg: "#fee2e2", fg: "#991b1b" },
    };
    return map[status] || { bg: "#f3f4f6", fg: "#111827" };
  };

  const filteredOrders = orders.filter((o) =>
    `${o.orderId || o._id || ""} ${o.customerId?.name || ""}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <Title>{t("orders.title")}</Title>
        <Sub>{t("orders.subtitle")}</Sub>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">{t("orders.overview")}</TabsTrigger>
          <TabsTrigger value="orders">{t("orders.ordersTab")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div style={{ marginTop: 24 }} className="space-y-6">
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, minWidth: 140 }}
              >
                <option value="7d">{t("orders.last7Days")}</option>
                <option value="30d">{t("orders.last30Days")}</option>
                <option value="90d">{t("orders.last90Days")}</option>
              </select>
            </div>
            <StatGrid>
              {stats.map((s) => {
                const Icon = s.icon;
                return (
                  <StatCard key={s.nameKey}>
                    <StatTop>
                      <div>
                        <StatName>{t(s.nameKey)}</StatName>
                        <StatValue>{s.value}</StatValue>
                      </div>
                      <IconBubble>
                        <Icon size={20} />
                      </IconBubble>
                    </StatTop>
                  </StatCard>
                );
              })}
            </StatGrid>

            <div style={{ marginTop: 32 }}>
              <Card style={{ padding: 20 }}>
                <h2 style={{ margin: 0, marginBottom: 16, fontSize: 18, fontWeight: 700 }}>
                  {period === "7d" ? t("orders.ordersOverTime7") : period === "90d" ? t("orders.ordersOverTime90") : t("orders.ordersOverTime30")}
                </h2>
                <div style={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis yAxisId="left" orientation="left" stroke="#4f46e5" />
                      <YAxis yAxisId="right" orientation="right" stroke="#0ea5e9" />
                      <Tooltip />
                      <Bar yAxisId="left" dataKey="orders" fill="#4f46e5" name={t("orders.ordersLabel")} />
                      <Bar yAxisId="right" dataKey="value" fill="#0ea5e9" name={t("orders.valueLabel")} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {(orderStats?.recentOrders?.length > 0) && (
                <Card style={{ padding: 20, marginTop: 24 }}>
                  <h2 style={{ margin: 0, marginBottom: 16, fontSize: 18, fontWeight: 700 }}>{t("orders.recentOrders")}</h2>
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {orderStats.recentOrders.slice(0, 8).map((o) => (
                      <li key={o._id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                        <button type="button" onClick={() => navigate(`/orders/${o._id}`)} style={{ background: "none", border: "none", cursor: "pointer", fontWeight: 500, fontSize: 14 }}>
                          #{o.orderId}
                        </button>
                        <span style={{ fontSize: 13, color: "#6b7280" }}>{o.customerId?.name || t("orders.guest")}</span>
                        <span style={{ fontSize: 13, color: "#6b7280" }}>{o.storeId?.name || "—"}</span>
                        <span style={{ fontWeight: 600 }}>
                        {rates
                          ? formatCurrency(ratesService.convert(o.total || 0, o.currency || baseCurrency, currency, rates), currency)
                          : formatCurrency(o.total || 0, o.currency || currency)}
                      </span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div style={{ marginTop: 24 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div style={{ maxWidth: 420 }}>
                <div style={{ position: "relative" }}>
                  <Search
                    size={16}
                    color="#6b7280"
                    style={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                  <Input
                    placeholder={t("orders.searchOrders")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ paddingLeft: 34 }}
                  />
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <Button $variant="outline" $size="sm">
                  <Filter size={14} />
                  {t("orders.moreFilters")}
                </Button>
                <Button $variant="outline" $size="sm">
                  <Download size={14} />
                  {t("orders.export")}
                </Button>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("orders.orderId")}</TableHead>
                    <TableHead>{t("orders.date")}</TableHead>
                    <TableHead>{t("orders.customer")}</TableHead>
                    <TableHead>{t("orders.store")}</TableHead>
                    <TableHead>{t("orders.status")}</TableHead>
                    <TableHead>{t("orders.items")}</TableHead>
                    <TableHead style={{ textAlign: "right" }}>{t("orders.total")}</TableHead>
                    <TableHead style={{ textAlign: "right" }}>{t("orders.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => {
                    const c = getStatusStyle(order.status);
                    return (
                      <TableRow key={order._id}>
                        <TableCell style={{ fontWeight: 600 }}>{order.orderId || order._id}</TableCell>
                        <TableCell>{order.date ? new Date(order.date).toLocaleDateString() : "—"}</TableCell>
                        <TableCell>{order.customerId?.name || order.customerId?.email || "—"}</TableCell>
                        <TableCell>{order.storeId?.name || "—"}</TableCell>
                        <TableCell>
                          <span
                            style={{
                              padding: "4px 10px",
                              borderRadius: 9999,
                              fontSize: 11,
                              fontWeight: 700,
                              background: c.bg,
                              color: c.fg,
                              textTransform: "capitalize",
                            }}
                          >
                            {order.status === "processing" ? t("orders.processing") : order.status === "pending" ? t("orders.pending") : order.status === "completed" ? t("orders.completed") : order.status === "cancelled" ? t("orders.cancelled") : order.status}
                          </span>
                        </TableCell>
                        <TableCell>{order.items}</TableCell>
                        <TableCell style={{ textAlign: "right" }}>
                          {typeof order.total === "number" ? "$" + order.total.toLocaleString() : order.total ?? "—"}
                        </TableCell>
                        <TableCell style={{ textAlign: "right" }}>
                          <Button
                            $variant="outline"
                            $size="sm"
                            style={{ marginRight: 6 }}
                            onClick={() => navigate(`/orders/${order._id}`)}
                          >
                            <Eye size={14} />
                            {t("orders.view")}
                          </Button>
                          <Button $variant="ghost" $size="icon">
                            <MoreVertical size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

